import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Row,
    Col,
    Typography,
    Space,
    Button,
    Spin,
    message,
} from "antd";
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    TeamOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import MoodQuestionnaire from "./MoodQuestionnaire";
import { MoodOption } from "./MoodMeterDashboard";

const { Title, Text } = Typography;

interface CompanyType {
    id: string | null;
    type: string;
    name: string;
}

interface SubmittedQuestionsData {
    submitted_question_ids: string[];
    submitted_today_ids: string[];
    total_submitted: number;
    submitted_today: number;
}

const MoodQuestionnairePage: React.FC = () => {
    const signature = localStorage.getItem("signature");
    const user = useSelector((state: any) => state.user.user);
    const navigate = useNavigate();

    const currentUserId = user?.userDetails?.id;
    const userName = user?.userDetails?.fname;
    const departmentId = user?.userDetails?.department;

    // State
    const [selectedCompany, setSelectedCompany] = useState<CompanyType>({
        id: user.company_id,
        type: localStorage.account_type,
        name: "",
    });
    const [companies, setCompanies] = useState<any[]>([]);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [submittedData, setSubmittedData] = useState<SubmittedQuestionsData>({
        submitted_question_ids: [],
        submitted_today_ids: [],
        total_submitted: 0,
        submitted_today: 0,
    });

    // Track if ALL questions are completed
    const [allQuestionsCompleted, setAllQuestionsCompleted] = useState<boolean>(false);

    // Fetch companies
    const fetchCompanies = async () => {
        try {
            const response = await api.post("userService/getAllCompanyList", {
                signature,
            });

            if (response.data?.msg === "Data Found") {
                const companiesData = response.data.data?.data || [];
                setCompanies(companiesData);

                const formattedOptions = companiesData.map((company: any) => ({
                    value: `${company.id}-${company.type}`,
                    label: `${company.name} - ${company.type}`,
                    companyName: company.name,
                    companyId: company.id,
                    companyType: company.type,
                }));
                setCompanyOptions(formattedOptions);

                if (!selectedCompany.id && companiesData.length > 0) {
                    const firstCompany = companiesData[0];
                    handleCompanyChange({
                        value: `${firstCompany.id}-${firstCompany.type}`,
                        label: `${firstCompany.name} - ${firstCompany.type}`,
                        companyName: firstCompany.name,
                        companyId: firstCompany.id,
                        companyType: firstCompany.type,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            message.error("Failed to load companies");
        }
    };

    // Fetch departments
    const fetchDepartments = async () => {
        try {
            const response = await api.post("userService/getDepartment", {
                signature,
            });

            if (response.data?.msg === "Data Found") {
                const deptData = response.data.data?.data || [];
                const formattedDepartments = deptData.map((dept: any) => ({
                    id: dept.key,
                    name: dept.Department,
                    key: dept.key,
                    Department: dept.Department,
                    NoOfEmployees: dept.NoOfEmployees
                }));
                setDepartments(formattedDepartments);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    };

    // Fetch submitted question IDs
    const fetchSubmittedQuestionIds = async (companyId: string, companyType: string) => {
        if (!companyId || !currentUserId) return;

        try {
            const response = await api.post("userService/getSubmittedQuestionIds", {
                signature,
            });

            if (response.data?.message?.[0]?.status === true) {
                const data = response.data.message[0].data;
                setSubmittedData({
                    submitted_question_ids: data.submitted_question_ids || [],
                    submitted_today_ids: data.submitted_today_ids || [],
                    total_submitted: data.total_submitted || 0,
                    submitted_today: data.submitted_today || 0,
                });
            }
        } catch (error) {
            console.error("Error fetching submitted question IDs:", error);
        }
    };

    // Handle company change
    const handleCompanyChange = async (selectedOption: any) => {
        if (!selectedOption) return;

        const [companyId, companyType] = selectedOption.value.split('-');
        const companyName = selectedOption.companyName;

        setSelectedCompany({
            id: companyId,
            type: companyType,
            name: companyName,
        });
        setAllQuestionsCompleted(false);

        localStorage.setItem("selectedCompanyId", companyId);
        localStorage.setItem("selectedCompanyType", companyType);
        localStorage.setItem("selectedCompanyName", companyName);

        await fetchSubmittedQuestionIds(companyId, companyType);
        message.success(`Switched to ${companyName}`);
    };

    // Handle mood submission
    const handleMoodSubmit = async (response: {
        questionId: string;
        selectedOption: MoodOption;
        note?: string;
    }) => {
        if (!selectedCompany.id) {
            message.error("Please select a company first");
            return;
        }

        try {
            const payload = {
                signature,
                question_id: response.questionId,
                selected_option_id: response.selectedOption.id,
                mood_text: response.selectedOption.text,
                score: response.selectedOption.score,
                note: response.note || "",
                department_id: departmentId,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                is_anonymous: true,
            };

            const apiResponse = await api.post("userService/submitMoodResponse", payload);

            if (apiResponse.data?.message?.[0]?.status === true) {
                fetchSubmittedQuestionIds(selectedCompany.id, selectedCompany.type);

                message.success({
                    content: "Mood submitted successfully!",
                    icon: "😊",
                    duration: 2,
                });
            } else {
                message.error(apiResponse.data?.msg || "Failed to submit mood");
            }
        } catch (error: any) {
            console.error("Error submitting mood:", error);
            message.error("Error submitting mood. Please try again.");
        }
    };

    const handleAllQuestionsCompleted = () => {
        setAllQuestionsCompleted(true);
    };

    const handleRefresh = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchCompanies(),
                fetchDepartments(),
                selectedCompany.id && fetchSubmittedQuestionIds(selectedCompany.id, selectedCompany.type),
            ]);
            message.info("Data refreshed");
        } catch (error) {
            console.error("Error refreshing data:", error);
            message.error("Failed to refresh data");
        } finally {
            setLoading(false);
        }
    }, [selectedCompany.id, selectedCompany.type]);

    const getCurrentCompanyValue = () => {
        if (!selectedCompany.id || !selectedCompany.type) {
            return companyOptions[0] || null;
        }

        const currentValue = `${selectedCompany.id}-${selectedCompany.type}`;
        return companyOptions.find(option => option.value === currentValue) || companyOptions[0];
    };

    useEffect(() => {
        const initData = async () => {
            setLoading(true);
            try {
                await fetchCompanies();
                await fetchDepartments();

                if (selectedCompany.id && selectedCompany.type) {
                    await fetchSubmittedQuestionIds(selectedCompany.id, selectedCompany.type);
                }
            } catch (error) {
                console.error("Error initializing data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (signature) {
            initData();
        }
    }, [signature]);

    useEffect(() => {
        if (selectedCompany.id && selectedCompany.type && currentUserId) {
            fetchSubmittedQuestionIds(selectedCompany.id, selectedCompany.type);
            setAllQuestionsCompleted(false);
        }
    }, [selectedCompany.id, selectedCompany.type, currentUserId]);

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content">
                    <div className="text-center py-5">
                        <Spin size="large" />
                        <div className="mt-3">
                            <Text type="secondary">Loading Daily Mood Check...</Text>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1" style={{ fontWeight: 600 }}>
                            <CheckCircleOutlined style={{ marginRight: 10 }} />
                            Daily Mood Check-in
                        </h2>
                        {submittedData.submitted_today > 0 && !allQuestionsCompleted && (
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                You've completed {submittedData.submitted_today} question{submittedData.submitted_today !== 1 ? 's' : ''} today
                            </Text>
                        )}
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        <div
                            className="dropdown me-2 mb-2"
                            title="Choose Company"
                            style={{ minWidth: "200px" }}
                        >
                            <CommonSelect
                                className="select"
                                options={companyOptions}
                                defaultValue={getCurrentCompanyValue()}
                                onChange={handleCompanyChange}
                                placeHolder="Select Company"
                                isDisabled={loading}
                            />
                        </div>
                    </div>
                </div>

                {/* Show completion screen ONLY when ALL questions are done */}
                {allQuestionsCompleted ? (
                    <Card className="mb-3">
                        <div className="text-center py-4">
                            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                            <Title level={4} style={{ marginBottom: 8 }}>🎉 All Done for Today!</Title>
                            <Text type="secondary">
                                You've completed all your daily mood check-ins. Thank you for your feedback!
                            </Text>
                            <div className="mt-4">
                                <Space size="middle">
                                    <Button
                                        size="large"
                                        onClick={handleRefresh}
                                        icon={<ReloadOutlined />}
                                    >
                                        Check for New Questions
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </Card>
                ) : (
                    <>
                        {/* Info Cards */}
                        <Row gutter={[16, 16]} className="mb-3">
                            <Col xs={24} sm={12} md={8}>
                                <Card size="small">
                                    <Space direction="vertical" size="small">
                                        <ClockCircleOutlined style={{ fontSize: 20, color: '#faad14' }} />
                                        <Text strong>Time Required</Text>
                                        <Text type="secondary">2-3 minutes</Text>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>

                        {/* Main Questionnaire Card */}
                        <Card
                            className="shadow-sm"
                            title={
                                <Space>
                                    <span>Today's Mood Check</span>
                                    {selectedCompany.name && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            for {selectedCompany.name}
                                        </Text>
                                    )}
                                </Space>
                            }
                            extra={
                                <Button
                                    size="small"
                                    onClick={handleRefresh}
                                    icon={<ReloadOutlined />}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            {!selectedCompany.id ? (
                                <div className="text-center py-4">
                                    <TeamOutlined style={{ fontSize: 32, color: '#ccc', marginBottom: 16 }} />
                                    <Title level={4} style={{ marginBottom: 8 }}>Select a Company</Title>
                                    <Text type="secondary">
                                        Please select a company from the dropdown above to start your daily mood check-in.
                                    </Text>
                                </div>
                            ) : (
                                <MoodQuestionnaire
                                    onSubmit={handleMoodSubmit}
                                    selectedCompany={selectedCompany}
                                    currentUser={{
                                        id: currentUserId,
                                        name: userName,
                                        department_id: departmentId,
                                    }}
                                    departments={departments}
                                    disabled={!selectedCompany.id}
                                    submittedTodayIds={submittedData.submitted_today_ids}
                                    onAllQuestionsCompleted={handleAllQuestionsCompleted} // Add this callback
                                />
                            )}
                        </Card>
                    </>
                )}

                {/* Footer */}
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2025 © CMS.</p>
                    <p>
                        Designed & Developed By{" "}
                        <Link to="#" className="text-primary">
                            ZYVO
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MoodQuestionnairePage;